import React, { useCallback, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import { PaginatorProps } from './types';
import ArrowButton from './components/ArrowButton/ArrowButton';
import { Container, Dots, PageButton, PageButtonContainer, PaginatorContainer } from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';

/**
 *
 * @param currentPage Number of the page currently selected
 * @param totalPages Quantity of pages available for pagination
 * @param onPageClick Method for changing the selected page (page:number)=>void
 * @param dataTestId Id for tests
 *
 */

const Paginator: React.FC<PaginatorProps> = ({
  currentPage,
  totalPages,
  onPageClick,
  dataTestId,
}) => {
  const { actualTheme } = useContext(ThemeContext);
  const getPagination = useCallback(() => {
    const res = [1];

    const page = currentPage + 1;
    let beforePage = page - 1;
    let afterPage = page + 1;

    if (page > 4 && totalPages >= 9) {
      res.push(-1);
    }

    beforePage = page - 2;
    if (page > totalPages - 5) {
      beforePage = totalPages - 6;
    }
    if (beforePage <= 0) {
      beforePage = 2;
    }

    afterPage = page + 2;
    if (page < 5) {
      afterPage = 7;
    }
    if (afterPage >= totalPages) {
      afterPage = totalPages - 1;
    }

    for (let plength = beforePage; plength <= afterPage; plength++) {
      if (plength > totalPages || plength <= 1) {
        continue;
      }
      res.push(plength);
    }

    if (afterPage < totalPages - 1) {
      res.push(-1);
    }

    if (totalPages > 1) {
      res.push(totalPages);
    }

    return res;
  }, [currentPage, totalPages]);

  const onClick = useCallback(
    (page: number) => () => {
      if (currentPage !== page) onPageClick(page);
    },
    [onPageClick, currentPage],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <Container data-testid={dataTestId}>
        <PaginatorContainer>
          <ArrowButton onClick={onClick(currentPage - 1)} disabled={currentPage === 0} />
          {getPagination().map((x, idx) => (
            <PageButtonContainer key={idx}>
              {x === -1 && <Dots>...</Dots>}
              {x !== -1 && (
                <PageButton
                  currentPage={currentPage}
                  page={x}
                  onClick={onClick(x - 1)}
                  role="presentation"
                >
                  {x}
                </PageButton>
              )}
            </PageButtonContainer>
          ))}
          <ArrowButton
            direction="right"
            onClick={onClick(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          />
        </PaginatorContainer>
      </Container>
    </ThemeProvider>
  );
};

export default Paginator;
